import { Dialog } from '@blueprintjs/core'
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  MiniMap,
  type ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import { QUERY_KEYS } from '~/api/query-client-keys'
import LeftArrowIcon from '~/components/icons/LeftArrowIcon'
import { generateFilterString, type ImageFilters } from './ImageManipulationControls'
import cs from './montage_hero.scss'

export interface ZoomPanState {
  scale: number
  positionX: number
  positionY: number
}

type ExpandedMontageModalProps = {
  isOpen: boolean
  onClose: () => void
  largeSpriteUrl: string | null
  stitchedImage: string | null
  imageFilters: ImageFilters
  zoomPanState?: ZoomPanState
  onZoomPanChange?: (state: ZoomPanState) => void
  /** Main view's viewport dimensions for coordinate transformation */
  mainViewportDimensions?: { width: number; height: number }
}

/**
 * Hook to validate and load a server-stitched well sprite URL.
 * Returns the blob URL via `data` if valid. If the sprite is not available
 * (404/403), the query will be in an error state with `data` undefined.
 * Callers should check `data` to determine availability.
 *
 * Note: We use GET (not HEAD) because S3 presigned URLs are method-specific.
 * A URL signed for GET will return 403 when accessed via HEAD.
 *
 * Blob URL lifecycle: Blob URLs are cached by React Query for 5 minutes.
 * We intentionally don't revoke them during navigation because React Query
 * may return cached blob URLs when navigating back to a previously viewed well.
 * Revoking would invalidate the cached URL, causing broken images.
 * Blob URLs are automatically cleaned up by the browser when the page unloads.
 */
export function useServerStitchedImage(url: string | null, enabled: boolean) {
  const cacheKey = url?.split('?')[0] ?? null

  return useQuery({
    queryKey: [QUERY_KEYS.SERVER_STITCHED_IMAGE, cacheKey],
    queryFn: async (): Promise<string> => {
      if (!url) throw new Error('No URL provided')

      const response = await fetch(url, { method: 'GET', cache: 'force-cache' })

      if (response.status === 404 || response.status === 403) {
        throw new Error('Server sprite not available')
      }

      if (!response.ok) {
        throw new Error(`Failed to load server sprite: ${response.status}`)
      }

      const blob = await response.blob()
      return URL.createObjectURL(blob)
    },
    enabled: enabled && !!url,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

export function ExpandedMontageModal({
  isOpen,
  onClose,
  largeSpriteUrl,
  stitchedImage,
  imageFilters,
  zoomPanState,
  onZoomPanChange,
  mainViewportDimensions,
}: ExpandedMontageModalProps) {
  const expandedTransformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const expandedViewportRef = useRef<HTMLDivElement>(null)
  const hasMountedRef = useRef(false)
  const [isContextReady, setIsContextReady] = useState(false)
  const [showExpandedMinimap, setShowExpandedMinimap] = useState(true)
  const [showExpandedBackButton, setShowExpandedBackButton] = useState(true)
  const minimapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const backButtonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMinimapHoveredRef = useRef(false)
  const rafRef = useRef<number | null>(null)

  const { data: validatedLargeSpriteUrl } = useServerStitchedImage(
    largeSpriteUrl,
    isOpen && !!largeSpriteUrl,
  )

  const startMinimapHideTimer = useCallback(() => {
    if (minimapTimerRef.current) clearTimeout(minimapTimerRef.current)
    minimapTimerRef.current = setTimeout(() => {
      if (!isMinimapHoveredRef.current) {
        setShowExpandedMinimap(false)
      }
    }, 1000)
  }, [])

  const startBackButtonHideTimer = useCallback(() => {
    if (backButtonTimerRef.current) clearTimeout(backButtonTimerRef.current)
    backButtonTimerRef.current = setTimeout(() => {
      setShowExpandedBackButton(false)
    }, 5000)
  }, [])

  useEffect(() => {
    if (isOpen) {
      setShowExpandedMinimap(true)
      setShowExpandedBackButton(true)
      isMinimapHoveredRef.current = false
      startMinimapHideTimer()
      startBackButtonHideTimer()
      rafRef.current = requestAnimationFrame(() => {
        // Guard against state updates after unmount
        if (rafRef.current === null) return
        hasMountedRef.current = true
        setIsContextReady(true)
      })
    }
    return () => {
      setIsContextReady(false)
      hasMountedRef.current = false
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (minimapTimerRef.current) clearTimeout(minimapTimerRef.current)
      if (backButtonTimerRef.current) clearTimeout(backButtonTimerRef.current)
    }
  }, [isOpen, startMinimapHideTimer, startBackButtonHideTimer])

  const handleExpandedInteraction = useCallback(() => {
    setShowExpandedMinimap(true)
    setShowExpandedBackButton(true)
    if (minimapTimerRef.current) clearTimeout(minimapTimerRef.current)
    if (backButtonTimerRef.current) clearTimeout(backButtonTimerRef.current)
    startMinimapHideTimer()
    startBackButtonHideTimer()
  }, [startMinimapHideTimer, startBackButtonHideTimer])

  const persistZoomPan = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      handleExpandedInteraction()
      if (!onZoomPanChange) return
      const { scale, positionX, positionY } = ref.state || {}
      if (scale == null) return

      // Transform coordinates from expanded view to main view
      const expandedWrapper = ref.instance.wrapperComponent
      const expandedContent = ref.instance.contentComponent
      if (!expandedWrapper || !expandedContent || !mainViewportDimensions) {
        // Fallback: sync scale only, centered
        onZoomPanChange({ scale, positionX: 0, positionY: 0 })
        return
      }

      const expandedWrapperRect = expandedWrapper.getBoundingClientRect()

      // The wrapper size IS the base content size at scale=1 (content fills wrapper)
      // So max pan = wrapper - wrapper * scale = wrapper * (1 - scale)
      const expandedMaxPanX = expandedWrapperRect.width * (1 - scale)
      const expandedMaxPanY = expandedWrapperRect.height * (1 - scale)

      // Normalize position as fraction of pan range
      const normalizedX = expandedMaxPanX !== 0 ? positionX / expandedMaxPanX : 0
      const normalizedY = expandedMaxPanY !== 0 ? positionY / expandedMaxPanY : 0

      // Calculate max pan range for main view
      // Main view content fills viewport at scale=1
      const mainMaxPanX =
        mainViewportDimensions.width - mainViewportDimensions.width * scale
      const mainMaxPanY =
        mainViewportDimensions.height - mainViewportDimensions.height * scale

      // Apply normalized position to main view's pan range
      const transformedX = normalizedX * mainMaxPanX
      const transformedY = normalizedY * mainMaxPanY

      onZoomPanChange({ scale, positionX: transformedX, positionY: transformedY })
    },
    [onZoomPanChange, handleExpandedInteraction, mainViewportDimensions],
  )

  const handleMinimapMouseEnter = useCallback(() => {
    isMinimapHoveredRef.current = true
    setShowExpandedMinimap(true)
    if (minimapTimerRef.current) clearTimeout(minimapTimerRef.current)
  }, [])

  const handleMinimapMouseLeave = useCallback(() => {
    isMinimapHoveredRef.current = false
    startMinimapHideTimer()
  }, [startMinimapHideTimer])

  const expandedImageUrl = validatedLargeSpriteUrl ?? stitchedImage
  const filterString = generateFilterString(imageFilters)
  const canShowOverlays = !!expandedImageUrl && isContextReady

  // Transform main view coordinates to expanded view coordinates on init
  const handleInit = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      if (!zoomPanState || !mainViewportDimensions) return

      const { scale, positionX, positionY } = zoomPanState
      if (scale === 1 && positionX === 0 && positionY === 0) return

      // Capture current state to verify it hasn't changed when timeout fires
      const capturedState = { scale, positionX, positionY }

      // Delay to ensure dialog has fully rendered at final size
      // The dialog needs time to expand to its full 90vw/90vh size
      setTimeout(() => {
        // Guard: verify component is still mounted and state hasn't changed
        const expandedWrapper = ref.instance.wrapperComponent
        if (!expandedWrapper || !hasMountedRef.current) return
        if (
          zoomPanState?.scale !== capturedState.scale ||
          zoomPanState?.positionX !== capturedState.positionX ||
          zoomPanState?.positionY !== capturedState.positionY
        ) {
          return
        }

        const expandedWrapperRect = expandedWrapper.getBoundingClientRect()

        // Calculate max pan range for main view (content fills viewport at scale=1)
        const mainMaxPanX = mainViewportDimensions.width * (1 - scale)
        const mainMaxPanY = mainViewportDimensions.height * (1 - scale)

        // Normalize position from main view
        const normalizedX = mainMaxPanX !== 0 ? positionX / mainMaxPanX : 0
        const normalizedY = mainMaxPanY !== 0 ? positionY / mainMaxPanY : 0

        // Calculate max pan range for expanded view (content fills wrapper at scale=1)
        const expandedMaxPanX = expandedWrapperRect.width * (1 - scale)
        const expandedMaxPanY = expandedWrapperRect.height * (1 - scale)

        // Apply normalized position to expanded view
        const transformedX = normalizedX * expandedMaxPanX
        const transformedY = normalizedY * expandedMaxPanY

        // Set the transform with the converted coordinates
        ref.setTransform(transformedX, transformedY, scale, 0)
      }, 50)
    },
    [zoomPanState, mainViewportDimensions],
  )

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className={cs.expandedDialog}
      canOutsideClickClose={true}
      canEscapeKeyClose={true}
      transitionDuration={0}
    >
      <div className={cs.expandedContent}>
        {expandedImageUrl ? (
          <TransformWrapper
            ref={expandedTransformRef}
            initialScale={zoomPanState?.scale ?? 1}
            initialPositionX={0}
            initialPositionY={0}
            minScale={1}
            maxScale={5}
            wheel={{
              step: 0.01,
              smoothStep: 0.01,
              wheelDisabled: false,
              touchPadDisabled: false,
            }}
            doubleClick={{ step: 1 }}
            zoomAnimation={{ animationTime: 150 }}
            centerOnInit={!zoomPanState}
            limitToBounds={true}
            disablePadding={true}
            onInit={handleInit}
            onZoomStop={persistZoomPan}
            onPanningStop={persistZoomPan}
            onPinchingStop={persistZoomPan}
          >
            <div className={cs.expandedTransform} ref={expandedViewportRef}>
              <TransformComponent
                wrapperClass={cs.expandedTransformWrapper}
                contentClass={cs.expandedTransformImage}
              >
                <img
                  src={expandedImageUrl}
                  alt='Expanded Montage'
                  crossOrigin='anonymous'
                  className={cs.expandedImage}
                  style={{
                    filter: filterString || undefined,
                  }}
                />
              </TransformComponent>
            </div>
            {/* Overlays stay inside TransformWrapper to satisfy MiniMap context */}
            {canShowOverlays ? (
              <>
                <section
                  aria-label='Minimap overview'
                  className={cx(cs.expandedMiniMapContainer, {
                    [cs.hiddenInteractive]: !showExpandedMinimap,
                  })}
                  onMouseEnter={handleMinimapMouseEnter}
                  onMouseLeave={handleMinimapMouseLeave}
                  onFocus={handleMinimapMouseEnter}
                  onBlur={handleMinimapMouseLeave}
                  aria-hidden={!showExpandedMinimap}
                  tabIndex={showExpandedMinimap ? 0 : -1}
                  style={{ transition: hasMountedRef.current ? undefined : 'none' }}
                >
                  <MiniMap width={150} height={150} borderColor='#ccc'>
                    <img
                      src={expandedImageUrl}
                      alt='Minimap'
                      crossOrigin='anonymous'
                      className={cs.miniMapImage}
                      style={{
                        filter: filterString || undefined,
                      }}
                    />
                  </MiniMap>
                </section>
                <button
                  type='button'
                  className={cx(cs.backButton, {
                    [cs.hiddenInteractive]: !showExpandedBackButton,
                  })}
                  onClick={onClose}
                  aria-label='Go back'
                  title='Go back'
                  aria-hidden={!showExpandedBackButton}
                  tabIndex={showExpandedBackButton ? 0 : -1}
                  style={{ transition: hasMountedRef.current ? undefined : 'none' }}
                >
                  <LeftArrowIcon className={cs.backIcon} />
                </button>
              </>
            ) : null}
          </TransformWrapper>
        ) : null}
      </div>
    </Dialog>
  )
}
